/**
 * Helper functions for crop zone calculations
 * Used by both PropertiesStep and CropZonesTable components
 */
import { Maths } from 'helpers/maths';
/**
 * Calculate coverage percent from applied acreage and total area
 * @param appliedAcreage - The applied acreage value
 * @param totalArea - The total area value
 * @returns Coverage percent (0-100+)
 */
export const calculateCoveragePercent = (
  appliedAcreage: number,
  totalArea: number
): number => {
  if (totalArea <= 0) return 0;
  return Maths.multiplyRound(
    Maths.divideSafeRound(appliedAcreage, totalArea),
    100
  );
};

/**
 * Calculate applied acreage from coverage percent and total area
 * @param coveragePercent - The coverage percent value (0-100+)
 * @param totalArea - The total area value
 * @returns Applied acreage
 */
export const calculateAppliedAcreage = (
  coveragePercent: number,
  totalArea: number
): number => {
  if (totalArea <= 0) return 0;
  return Maths.divideSafeRound(
    Maths.multiplyRound(totalArea, coveragePercent),
    100
  );
};

/**
 * Calculate total area from an array of crop zones
 * @param cropZones - Array of crop zones with area property
 * @returns Total area sum
 */
export const calculateTotalArea = (
  cropZones: Array<{ area?: number }>
): number => {
  return cropZones?.reduce((sum, zone) => sum + (zone.area || 0), 0) || 0;
};

/**
 * Calculate total applied acreage from an array of crop zones
 * @param cropZones - Array of crop zones with appliedAcreage property
 * @returns Total applied acreage sum
 */
export const calculateTotalAppliedAcreage = (
  cropZones: Array<{ appliedAcreage?: number; area?: number }>
): number => {
  return (
    cropZones?.reduce(
      (sum, zone) => sum + (zone.appliedAcreage || zone.area || 0),
      0
    ) || 0
  );
};

/**
 * Calculate average coverage percent from an array of crop zones
 * @param cropZones - Array of crop zones with coveragePercent property
 * @returns Average coverage percent
 */
export const calculateAverageCoveragePercent = (
  cropZones: Array<{ coveragePercent?: number; appliedAcreage: number }>
): number => {
  if (!cropZones || cropZones.length === 0) return 100;

  const totalArea = cropZones.reduce((sum, zone) => {
    const percentage = Maths.divideSafeRound(zone?.coveragePercent || 100, 100);
    const total = Maths.divideSafeRound(zone.appliedAcreage, percentage);

    return Maths.plusRound(sum, total);
  }, 0);

  const totalAppliedAcreage = cropZones.reduce((sum, zone) => {
    return Maths.plusRound(sum, zone.appliedAcreage);
  }, 0);

  return Maths.multiplyRound(
    Maths.divideSafeRound(totalAppliedAcreage, totalArea),
    100
  );
};

/**
 * Distribute total applied acreage proportionally across crop zones
 * @param cropZones - Array of crop zones
 * @param totalAppliedAcreage - Total acreage to distribute
 * @param coveragePercent - Coverage percent value
 * @param distributionMode - 'proportional' (distribute acres proportionally) or 'uniform' (same coverage for all)
 * @returns Updated crop zones with new applied acreage and coverage percent
 */
export const distributeTotalAcreageProportionally = <
  T extends { area?: number }
>(
  cropZones: T[],
  totalAppliedAcreage: number,
  coveragePercent: number,
  distributionMode: 'proportional' | 'uniform' = 'proportional'
): Array<T & { appliedAcreage: number; coveragePercent: number }> => {
  const totalArea = calculateTotalArea(cropZones);

  if (distributionMode === 'uniform') {
    // All zones get the same coverage percent, calculate applied acreage accordingly
    return cropZones.map(zone => {
      const zoneArea = zone.area || 0;
      const newAppliedAcreage = calculateAppliedAcreage(
        coveragePercent,
        zoneArea
      );

      return {
        ...zone,
        appliedAcreage: newAppliedAcreage,
        coveragePercent
      };
    });
  }

  // Proportional mode: distribute total acreage proportionally by area
  return cropZones.map(zone => {
    const zoneArea = zone.area || 0;

    // Calculate this zone's proportion of total area
    const areaProportion =
      totalArea > 0 ? Maths.divide(zoneArea, totalArea) : 0;

    // Calculate this zone's applied acreage based on total and proportion
    const newAppliedAcreage = Maths.multiplyRound(
      totalAppliedAcreage,
      areaProportion
    );

    // Calculate new coverage percent for this zone
    const newCoveragePercent = calculateCoveragePercent(
      newAppliedAcreage,
      zoneArea
    );

    return {
      ...zone,
      appliedAcreage: newAppliedAcreage,
      coveragePercent: newCoveragePercent
    };
  });
};
